
import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { checkRouteAccess } from "@/utils/routeUtils";
import { SPECIAL_ADMIN_EMAILS } from "@/contexts/auth/constants";

interface ProtectedRouteProps {
  children: React.ReactNode;
  path?: string; // Optional path to check (uses current location if not provided)
}

const ProtectedRouteV2: React.FC<ProtectedRouteProps> = ({ children, path }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();
  const params = useParams();
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Extract the current route pattern, replacing any ID params with :id
  const currentPath = useMemo(() => {
    if (path) return path;

    let derivedPath = location.pathname;
    
    // Replace numeric segments that likely represent IDs with :id
    Object.entries(params).forEach(([key, value]) => {
      if (value && /^\d+$/.test(value)) {
        const placeholder = `:${key}`;
        derivedPath = derivedPath.replace(`/${value}`, `/${placeholder}`);
      }
    });
    
    return derivedPath;
  }, [location.pathname, params, path]);

  // Set a timeout to prevent infinite loading
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.log("Authentication check timed out, forcing navigation to login page");
        setLoadingTimeout(true);
      }
    }, 15000); // 15 seconds timeout

    return () => clearTimeout(timeoutId);
  }, [loading]);

  // Check route access when user is loaded
  useEffect(() => {
    const verifyAccess = async () => {
      if (!user) return;
      
      // Admins and special emails always have access
      if (isAdmin || (user.email && SPECIAL_ADMIN_EMAILS.includes(user.email.toLowerCase()))) {
        setHasAccess(true);
        setAccessChecked(true);
        return;
      }
      
      // Check route access from database
      const access = await checkRouteAccess(user.id, currentPath);
      setHasAccess(access);
      setAccessChecked(true);
      
      if (!access) {
        toast.error("You don't have permission to access this page");
      }
    };
    
    if (!loading && user) {
      verifyAccess();
    } else if (!loading && !user) {
      setAccessChecked(true);
    }
  }, [user, loading, isAdmin, currentPath]);

  // Force redirect to login if loading times out
  if (loadingTimeout) {
    console.log("Loading timed out, redirecting to login page");
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // If we're still loading the auth state, show a loading indicator
  if (loading || !accessChecked) {
    return (
      <div className="flex h-[calc(100vh-100px)] items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Spinner size="md" />
          <p className="text-sm text-muted-foreground">
            Loading authentication...
          </p>
          <p className="text-xs text-muted-foreground mt-4">
            If this takes too long, <a href="/auth" className="text-blue-500 hover:underline">click here to login again</a>
          </p>
        </div>
      </div>
    );
  }

  // If the user is not authenticated, redirect to the login page
  if (!user) {
    console.log("User not authenticated, redirecting to login");
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // If the user doesn't have access to this route, redirect to home
  if (!hasAccess) {
    console.log(`Access denied for path: ${currentPath}`);
    return <Navigate to="/" replace />;
  }

  // Render children if authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRouteV2;
